/**
 * "Coming up": what the skill's date automations start in the next 30 days,
 * soonest first. "Nov 20 · Ali Khan · RFE response due in 14 days". A row
 * that shouldn't start a run can be skipped; skipped rows say by whom.
 */
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState, type FC } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { SectionHeader } from "@/components/ui/page-header";
import { firmKnowledgeApi, type FirmScope } from "@/api/firm-knowledge";
import { todayDateOnly } from "@/lib/dateOnly";

import type {
  FirmSkill,
  UpcomingAutomationItem
} from "@/features/firmKnowledge/types";
import { inDays } from "./AutomationEditor";
import { isDateAction, shortDate } from "./skillModel";

type Props = {
  skill: FirmSkill;
  scope: FirmScope;
  /** Member names by uid, for "Skipped by". */
  nameOf?: (uid: string) => string | undefined;
};

/** "RFE response due in 14 days"; "Passport expires in 60 days". */
export const upcomingReason = (row: UpcomingAutomationItem): string => {
  const who = row.who && row.who !== row.client ? `${row.who}'s ` : "";
  const label = row.label || "Item";
  const keepCase = label === label.toUpperCase() || label.startsWith("I-");
  const doc = who && !keepCase ? label.toLowerCase() : label;
  if (row.action === "client.priority_date_current") {
    return `${who}${label} is current`;
  }
  const verb = row.action === "client.document_expiring" ? "expires" : "due";
  return `${who}${doc} ${verb} ${inDays(row.days_left)}`;
};

/** "today" for a run that starts now, else "Nov 20". */
const startsLabel = (iso: string, today: string): string =>
  iso <= today ? "Today" : shortDate(`${iso}T12:00:00`);

export const UpcomingAutomations: FC<Props> = ({ skill, scope, nameOf }) => {
  const watched = skill.triggers.some(
    (t) => t.enabled && t.kind === "event" && isDateAction(t.event?.action)
  );
  const [rows, setRows] = useState<UpcomingAutomationItem[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    firmKnowledgeApi
      .listUpcomingAutomations(skill.id, scope)
      .then((items) => {
        if (!cancelled) setRows(items);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [skill.id, scope]);

  useEffect(() => {
    if (!watched) return undefined;
    return load();
  }, [watched, load, skill.triggers]);

  if (!watched || skill.status !== "active") return null;

  const skip = async (row: UpcomingAutomationItem) => {
    setBusy(row.item_key);
    try {
      await firmKnowledgeApi.skipUpcomingAutomation(
        skill.id,
        row.trigger_id,
        row.item_key,
        scope
      );
      setRows((cur) =>
        (cur ?? []).map((r) =>
          r.item_key === row.item_key && r.trigger_id === row.trigger_id
            ? { ...r, skipped_by: "you" }
            : r
        )
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not skip it.");
    } finally {
      setBusy(null);
    }
  };

  const today = todayDateOnly();
  return (
    <section className="flex flex-col gap-3" data-testid="skill-upcoming">
      <SectionHeader title="Coming Up" />
      {rows === null ? (
        <Loader2 className="size-4 animate-spin text-fg-3" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-fg-3">Nothing in the next 30 days.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-line-1 rounded-xl ring-1 ring-line-2">
          {rows.map((row) => {
            const skipped = row.skipped_by
              ? nameOf?.(row.skipped_by) ?? row.skipped_by
              : null;
            return (
              <li
                key={`${row.trigger_id}:${row.item_key}`}
                className="flex items-center gap-2 px-4 py-2 text-base"
                data-testid="skill-upcoming-row"
              >
                <span className="w-14 shrink-0 text-sm font-medium text-fg-3">
                  {startsLabel(row.starts_on, today)}
                </span>
                <span
                  className={
                    skipped
                      ? "min-w-0 flex-1 truncate text-fg-3 line-through"
                      : "min-w-0 flex-1 truncate text-fg-2"
                  }
                >
                  <span className="text-fg">{row.client ?? "A client"}</span> ·{" "}
                  {upcomingReason(row)}
                </span>
                {skipped ? (
                  <span className="text-sm text-fg-3">
                    Skipped by {skipped}
                  </span>
                ) : (
                  <Button
                    size="xs"
                    variant="ghost"
                    disabled={busy !== null}
                    onClick={() => void skip(row)}
                    data-testid="skill-upcoming-skip"
                  >
                    Skip
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
